import { highlightCode, tagHighlighter, tags } from "@lezer/highlight";
import Divider from "@mui/material/Divider";
import { TooltipProps } from "@mui/material/Tooltip";
import cn from "classnames";
import React from "react";

import { buildFireboltSql } from "pages/DevelopWorkspace/Editor/Document/DocumentContent/DocumentEditor/dialect/dialect";

import Tooltip from "components/Tooltip";

import styles from "./styles.module.scss";

type Props = {
  title: string;
  content: string;
  children: React.ReactElement<any, any>;
  placement?: TooltipProps["placement"];
  tooltipClassName?: string;
  open?: boolean;
  controlledProps?: {
    open?: boolean;
    onOpen?: () => void;
    onClose?: () => void;
  };
  footer?: React.ReactElement<any, any>;
  footerHidden?: boolean;
  enterDelay?: number;
  enterNextDelay?: number;
  fullContent?: boolean;
  plainContent?: boolean; // turn off syntax highlighting
};

export const cutContent = (content: string) =>
  content
    .trim()
    .split(/\r\n|\r|\n/)
    .splice(0, 10)
    .join("\n");

const highlighter = tagHighlighter(
  [
    {
      tag: tags.keyword,
      class: "ͼb",
    },
    {
      tag: tags.string,
      class: "ͼe",
    },
    {
      tag: tags.comment,
      class: "ͼm",
    },
    {
      tag: tags.number,
      class: "ͼd",
    },
    {
      tag: tags.bool,
      class: "ͼd",
    },
    {
      tag: tags.typeName,
      class: "ͼi",
    },
    {
      tag: tags.standard(tags.name),
      class: "ͼfn",
    },
    {
      tag: tags.special(tags.string),
      class: "ͼf",
    },
  ],
  {}
);

export function getHighlightedHTML(content: string) {
  let html = "";

  highlightCode(
    content,
    buildFireboltSql().language.parser.parse(content),
    highlighter,
    (text, classes) => {
      html += `<span class="${classes}">${text}</span>`;
    },
    () => {
      html += "<br/>";
    }
  );

  return html;
}

export const HintTooltip: React.FC<Props> = props => {
  const {
    children,
    footer = null,
    footerHidden = false,
    fullContent = false,
    plainContent = false,
    enterDelay = 500,
    enterNextDelay = 500,
    title,
    content,
    placement,
    tooltipClassName,
    controlledProps = {},
  } = props;

  const contentToHighlight = fullContent ? content : cutContent(content);
  const element = title ? (
    <div
      data-testid="script-hint"
      className={styles.wrapper}
    >
      <div className={styles.title}>{title}</div>
      {content && (
        <>
          <Divider
            key="divider-content"
            classes={{ root: cn(styles.divider, styles.forced) }}
          />
          {plainContent ? (
            <div
              data-testid="script-hint-content"
              className={cn(styles.query, styles.plain, {
                [styles.fullContent]: fullContent,
              })}
            >
              {contentToHighlight}
            </div>
          ) : (
            <div
              data-testid="script-hint-content"
              className={cn(styles.query, {
                [styles.fullContent]: fullContent,
              })}
              // eslint-disable-next-line react/no-danger -- html is sanitized
              dangerouslySetInnerHTML={{
                __html: getHighlightedHTML(contentToHighlight),
              }}
            />
          )}
        </>
      )}
      {footer && !footerHidden && (
        <>
          <Divider
            key="divider-footer"
            classes={{ root: cn(styles.divider, styles.forced) }}
          />
          {footer}
        </>
      )}
    </div>
  ) : null;

  return (
    <Tooltip
      classes={{
        arrow: cn(styles.arrow, styles.forced),
        tooltip: cn(styles.tooltip, styles.forced, tooltipClassName),
      }}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      title={element}
      placement={placement}
      {...controlledProps}
      arrow
    >
      {children}
    </Tooltip>
  );
};
